import { useMemo } from 'react';

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';

import { RiArrowLeftSLine } from '@remixicon/react';

import { SimpleAutocomplete } from '../SimpleAutocomplete';
import { useGroupedAutocompleteContext } from './context';
import { useStyles } from './styles';
import type { GroupedAutocompleteItem } from './types';

type GroupedAutocompleteItemsProps = {
  items: GroupedAutocompleteItem[];
  loading?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
};

export const GroupedAutocompleteItems = ({
  items,
  loading,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items available',
}: GroupedAutocompleteItemsProps) => {
  const {
    activeGroup,
    onActiveGroupChange,
    selectedItems,
    onSelectedItemsChange,
    groupItemsRef,
  } = useGroupedAutocompleteContext();
  const classes = useStyles();

  const activeGroupLabel = useMemo(() => {
    if (!activeGroup) return undefined;
    return groupItemsRef.current.find(g => g.value === activeGroup)?.label;
  }, [activeGroup, groupItemsRef]);

  const activeGroupSelection = useMemo(
    () => (activeGroup ? (selectedItems[activeGroup] ?? []) : []),
    [selectedItems, activeGroup],
  );

  const sortedItems = useMemo(() => {
    if (!activeGroup) return items;
    const selectionSet = new Set(activeGroupSelection);
    const checked: typeof items = [];
    const rest: typeof items = [];
    for (const item of items) {
      if (selectionSet.has(item.value)) {
        checked.push(item);
      } else {
        rest.push(item);
      }
    }
    return [...checked, ...rest];
  }, [items, activeGroup, activeGroupSelection]);

  if (activeGroup === null) return null;

  return (
    <SimpleAutocomplete.Root
      items={sortedItems}
      multiple
      value={activeGroupSelection}
      onSelect={itemId => {
        const current = selectedItems[activeGroup] ?? [];
        const next = current.includes(itemId)
          ? current.filter(v => v !== itemId)
          : [...current, itemId];
        if (next.length > 0) {
          onSelectedItemsChange({ ...selectedItems, [activeGroup]: next });
        } else {
          const { [activeGroup]: _, ...rest } = selectedItems;
          onSelectedItemsChange(rest);
        }
        return true;
      }}
    >
      {({ items: filteredItems, getListboxProps, getOptionProps }) => (
        <Box className={classes.root}>
          <SimpleAutocomplete.Input
            autoFocus
            placeholder={searchPlaceholder}
            margin="dense"
          />
          <Box className={classes.header}>
            <IconButton
              size="small"
              onClick={() => onActiveGroupChange(null)}
              aria-label="Back"
            >
              <RiArrowLeftSLine size={18} />
            </IconButton>
            <Typography variant="body2" className={classes.headerLabel} noWrap>
              {activeGroupLabel || activeGroup}
            </Typography>
          </Box>
          <Box className={classes.list}>
            {loading ? (
              <Box className={classes.centered}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <>
                <SimpleAutocomplete.Empty>
                  {items.length === 0 ? emptyMessage : 'No matches'}
                </SimpleAutocomplete.Empty>
                {filteredItems.length > 0 && (
                  <List {...getListboxProps()}>
                    {filteredItems.map((item, i) => {
                      const isSelected = activeGroupSelection.includes(
                        item.value,
                      );
                      return (
                        <ListItem
                          key={item.value}
                          button
                          dense
                          disableGutters
                          {...getOptionProps(i)}
                          selected={false}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={item.label || item.value}
                            primaryTypographyProps={{
                              variant: 'body2',
                              noWrap: true,
                              title: item.label || item.value,
                            }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </>
            )}
          </Box>
        </Box>
      )}
    </SimpleAutocomplete.Root>
  );
};
